/**
 * Shared utility to sanitize mermaid diagram content before rendering.
 * Handles:
 * 1. Single-line content from S3/backend (inserts proper newlines)
 * 2. Special characters (%, ', =, parentheses) that break mermaid parsing
 * 3. Style lines with hex colors that should NOT be quoted
 */
export function cleanMermaidContent(raw: string): string {
  let cleaned = raw.trim();

  // Strip markdown code fences
  const fenceMatch = cleaned.match(/^```(?:mermaid)?\s*\n?([\s\S]*?)\n?```$/i);
  if (fenceMatch) {
    cleaned = fenceMatch[1].trim();
  }

  // === STEP 0: Strip template placeholder braces ===
  // {{RUN_DATE}} and {RUN_DATE} are SQL template placeholders that appear in
  // diagram labels. The curly braces are Mermaid shape delimiters and break
  // parsing. Replace with just the placeholder name — visually equivalent.
  // Only target double-brace {{...}} (always a template) and single-brace
  // {UPPER_CASE} patterns (template vars are uppercase with underscores).
  cleaned = cleaned.replace(/\{\{(\w+)\}\}/g, '$1');
  cleaned = cleaned.replace(/\{([A-Z][A-Z_0-9]*_[A-Z_0-9]+)\}/g, '$1');

  // === STEP 1: Insert newlines so each statement is on its own line ===
  // This is critical for content that arrives as a single long line from S3.

  // Newline after flowchart/graph direction declaration
  cleaned = cleaned.replace(
    /((?:flowchart|graph)\s+(?:TD|TB|BT|LR|RL))\s+/i,
    '$1\n'
  );

  // Insert newline before "style " declarations (they must be on their own line)
  cleaned = cleaned.replace(/\s{2,}(style\s+)/g, '\n$1');

  // Insert newline before any node ID that starts a new statement.
  // Pattern: after a pipe-close "|", bracket-close "]", brace-close "}", or paren-close ")"
  // followed by whitespace and then a capital letter node ID followed by arrow or bracket/brace
  // We need to be careful not to break inside labels.
  // Strategy: split on multi-space gaps between statements.
  // Mermaid single-line content from S3 uses multiple spaces (4+) as statement separators.
  cleaned = cleaned.replace(/\s{3,}/g, '\n');

  // === STEP 1b: Normalize doubled quotes inside node labels ===
  // LLMs sometimes emit ["" ... ""] or ("" ... ""); collapse to [" ... "]
  // Use [\s\S]* instead of [^"]* so the match spans newlines inside labels.
  cleaned = cleaned.replace(/\[""([\s\S]*?)""\]/g, '["$1"]');
  cleaned = cleaned.replace(/\(""([\s\S]*?)""\)/g, '("$1")');
  cleaned = cleaned.replace(/\{""([\s\S]*?)""\}/g, '{"$1"}');

  // === STEP 1c: Fix nested shape braces from LLM output ===
  // LLMs generate patterns like {"{ ... "}} or {"{text}"} which break Mermaid.
  // Flatten to {"text"} by stripping inner braces/parens/brackets from shape labels.
  cleaned = cleaned.replace(
    /\{"?\{([\s\S]*?)\}"?\}/g,
    (_match, inner: string) => `{"${escapeMermaid(inner.replace(/"/g, ''))}"}`
  );
  cleaned = cleaned.replace(
    /\("?\(([\s\S]*?)\)"?\)/g,
    (_match, inner: string) => `("${escapeMermaid(inner.replace(/"/g, ''))}")`
  );

  // === STEP 2: Process each line - quote labels with special chars ===
  cleaned = cleaned
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '';

      // Skip style/classDef/class/end lines entirely
      if (/^(style|classDef|class|end)\b/i.test(trimmed)) {
        return trimmed;
      }

      // Handle subgraph lines: escape the label inside [...] but keep the line structure
      if (/^subgraph\b/i.test(trimmed)) {
        // subgraph ID["label"] or subgraph ID[label]
        let result = trimmed;
        // Escape already-quoted subgraph labels: subgraph X["..."]
        result = result.replace(/\["([^"]*)"\]/g, (_match, content: string) => {
          return `["${escapeMermaid(content)}"]`;
        });
        // Quote and escape unquoted subgraph labels with special chars: subgraph X[label]
        result = result.replace(/\[([^"\]][^\]]*)\]/g, (_match, content: string) => {
          const t = content.trim();
          if (!needsQuoting(t)) return `[${content}]`;
          return `["${escapeMermaid(t)}"]`;
        });
        return result;
      }

      // Process the line: quote pipe labels and node labels that need it
      let result = trimmed;

      // Quote pipe edge labels: |content| -> |"content"|
      result = result.replace(/\|([^"|][^|]*)\|/g, (_match, content: string) => {
        const t = content.trim();
        if (!needsQuoting(t)) return `|${content}|`;
        return `|"${escapeMermaid(t)}"|`;
      });

      // Quote square bracket labels: [content] -> ["content"]
      // Skip already-quoted: ["..."]
      result = result.replace(/\[([^"\]][^\]]*)\]/g, (_match, content: string) => {
        const t = content.trim();
        if (!needsQuoting(t)) return `[${content}]`;
        return `["${escapeMermaid(t)}"]`;
      });

      // Quote curly brace labels: {content} -> {"content"}
      result = result.replace(/\{([^"}][^}]*)\}/g, (_match, content: string) => {
        const t = content.trim();
        if (!needsQuoting(t)) return `{${content}}`;
        return `{"${escapeMermaid(t)}"}`;
      });

      return result;
    })
    .filter((line) => line !== '')
    .join('\n');

  // === STEP 3: Escape stray curly braces inside already-quoted labels ===
  // Patterns like ["...{RUN_DATE}..."] or ("...{text}...") break Mermaid
  // because { } are shape delimiters. Use numeric char refs #123; / #125;.
  cleaned = cleaned.replace(
    /(\[")([\s\S]*?)("\])/g,
    (_m, open: string, inner: string, close: string) =>
      `${open}${inner.replace(/\{/g, '#123;').replace(/\}/g, '#125;')}${close}`
  );
  cleaned = cleaned.replace(
    /(\(")([\s\S]*?)("\))/g,
    (_m, open: string, inner: string, close: string) =>
      `${open}${inner.replace(/\{/g, '#123;').replace(/\}/g, '#125;')}${close}`
  );
  cleaned = cleaned.replace(
    /(\{")([\s\S]*?)("\})/g,
    (_m, open: string, inner: string, close: string) =>
      `${open}${inner.replace(/\{/g, '#123;').replace(/\}/g, '#125;')}${close}`
  );

  return cleaned;
}

/** Characters that require the label to be wrapped in double quotes */
function needsQuoting(text: string): boolean {
  // %, ', =, (, ), {, }, <br/>, <, >, &  — but NOT # alone (used in style hex colors,
  // but style lines are already skipped above)
  return /[%'=(){}]|<br\s*\/?>|<|>|&/.test(text);
}

/** Escape special chars inside a quoted mermaid label */
function escapeMermaid(text: string): string {
  return text
    .replace(/%/g, '#percnt;')
    .replace(/'/g, '#apos;')
    .replace(/<br\s*\/?>/gi, '\x00BR\x00')   // protect <br/>
    .replace(/</g, '#lt;')
    .replace(/>/g, '#gt;')
    .replace(/\x00BR\x00/g, '<br/>')          // restore <br/>
    .replace(/\(/g, '#lpar;')
    .replace(/\)/g, '#rpar;')
    .replace(/\{/g, '#123;')
    .replace(/\}/g, '#125;');
}
